import * as v from './rules';

/** Mirrors ActivationDtos and the server-side batch and allocation ceilings. */
export const LIMITS = { reference: 40, batch: 10000, allocation: 2000 };

/** The alphabet the generator draws from — 0, 1, O, I and L are deliberately absent. */
export const CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

const wholeNumber = (label, max) => (value) => {
  const text = String(value ?? '').trim();
  if (!text) return `${label} is required`;
  if (!/^\d+$/.test(text)) return `${label} must be a whole number`;
  const amount = Number(text);
  if (amount < 1) return `${label} must be at least 1`;
  if (amount > max) return `${label} cannot be more than ${max.toLocaleString()}`;
  return null;
};

export const quantity = wholeNumber('Quantity', LIMITS.batch);
export const allocationQuantity = wholeNumber('Quantity', LIMITS.allocation);

export const titleId = v.required('Title');
export const schoolId = v.required('School');

/** Blank is valid: the server derives a reference from the title code and the date. */
export const reference = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return null;
  if (text.length > LIMITS.reference) {
    return `Reference must be ${LIMITS.reference} characters or fewer`;
  }
  if (!/^[A-Za-z0-9-]+$/.test(text)) return 'Use letters, numbers and hyphens only';
  return null;
};

/**
 * The student's Enter Activation Code field.
 *
 * <p>Checked here before sending so a typo answers instantly, and so a plainly
 * malformed code does not spend one of the student's throttled attempts.
 */
export const activationCode = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return 'Enter the activation code your teacher gave you';

  const flat = text.toUpperCase().replace(/[\s-]/g, '');
  // Naming the excluded characters is the useful message: a student who typed O
  // meant Q, and a generic "invalid code" would leave them retyping the same thing.
  if (/[01OIL]/.test(flat)) {
    return 'Activation codes never contain 0, 1, O, I or L — check those characters';
  }
  // Characters before length: someone who typed dots instead of hyphens has the
  // right twelve characters, and telling them the code is the wrong length would
  // send them counting instead of looking at the separators.
  if ([...flat].some((c) => !CODE_ALPHABET.includes(c))) {
    return 'Use only the letters and numbers printed on your code, separated by hyphens';
  }
  if (flat.length !== 12) return 'An activation code has 12 characters, like ABCD-EFGH-JKMN';
  return null;
};

/** Every activation failure is about the one field on the screen. */
export const ACTIVATION_SERVER_CODES = {
  invalid_code: 'code',
  code_already_used: 'code',
  code_not_issued: 'code',
  code_other_school: 'code',
  title_already_activated: 'code',
};

export const SERVER_CODES = {
  reference_taken: 'reference',
  title_inactive: 'titleId',
  insufficient_codes: 'quantity',
  code_pool_exhausted: 'quantity',
};

/**
 * The canonical form of a typed code, matching ActivationCodeGenerator.normalize:
 * whitespace and hyphens are stripped and the rest is upper-cased, but nothing is
 * substituted — a mistyped O must stay invalid rather than become a different code.
 */
export function normalizeCode(input) {
  const flat = String(input ?? '').toUpperCase().replace(/[\s-]/g, '');
  if (flat.length !== 12 || [...flat].some((c) => !CODE_ALPHABET.includes(c))) return flat;
  return `${flat.slice(0, 4)}-${flat.slice(4, 8)}-${flat.slice(8, 12)}`;
}
